import { datadogRum } from '@datadog/browser-rum'
import { useQuery } from '@tanstack/react-query'
import { Amplify, Auth } from 'aws-amplify'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import type { CognitoUserSession } from 'amazon-cognito-identity-js'
import type { FC, PropsWithChildren } from 'react'
import type { Customer } from 'types'
import type { UserSession } from 'utils/auth-utils'

type ContextType = {
  // Auth
  isSignedIn: boolean
  signIn: (credentials: { username: string; password: string }) => Promise<void>
  signOut: () => Promise<void>
  // User
  session?: CognitoUserSession
  user?: UserSession['user']
  reloadUser: () => void
  // Notifications
  shouldNotifyNewVersion: boolean
  setPrevVersionUsed: React.Dispatch<React.SetStateAction<string>>
  // Customers
  customers: Customer[]
  selectedCustomer?: Customer
  setSelectedCustomer: (customer: Customer) => void
}

const configureAuth = () =>
  Amplify.configure({
    aws_cognito_region: import.meta.env.VITE_AWS_COGNITO_REGION,
    aws_user_pools_id: import.meta.env.VITE_AWS_USER_POOLS_ID,
    aws_user_pools_web_client_id: import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID,
    aws_mandatory_sign_in: 'enable',
  })

export const getCurrentSession = async (includeUserInfo = false): Promise<UserSession> => {
  try {
    // Ensure Cognito is configured on first call to get session
    if (!Amplify.Auth.userPool) {
      configureAuth()
    }

    const session = await Auth.currentSession()
    const user = includeUserInfo ? await Auth.currentUserInfo() : null
    return {
      session,
      user: user ? { ...user.attributes, 'cognito:groups': session.getAccessToken().payload['cognito:groups'] } : null,
    }
  } catch (error) {
    return {}
  }
}

// The only time we make an API call like this since the useApi hook is not available in the context
const getCustomers = async (): Promise<Customer[]> => {

  const mockCustomers: Customer[] = [
    {
      customerGuid: '6861bea6-b156-4c6c-8eef-7adaa04287e6',
      externalId: '101123',
      customerName: 'Pat Cummins',
      customerId: 1872,
      countrycode: 'UK',
      status: 1,
      hasUsers: false,
      customerTypeId: 0,
      customerTypeFlags: 0,
      timeZoneID: 26,
      realmID: 2,
      customerNameWithExtIDs: 'Pat Cummins',
      realm: {
        id: 1,
        name: 'ATHLETE',
        connectorUrl: 'https://test-services.resultsvault.com/RVVideoConnector_TEST_UK',
        compConnectorOptions: {
          canExposeStreamGuid: 0,
          canExposeExtendedCompData: 0,
        },
      },
    },
    {
      customerGuid: '47df1e19-cb34-4f35-8e25-1c8ab2cfa4b1',
      externalId: '101124',
      customerName: 'Jessica Ennis',
      customerId: 1873,
      countrycode: 'AUS',
      status: 1,
      hasUsers: false,
      customerTypeId: 0,
      customerTypeFlags: 0,
      timeZoneID: 26,
      realmID: 2,
      customerNameWithExtIDs: 'Jessica Ennis',
      realm: {
        id: 1,
        name: 'ATHLETE',
        connectorUrl: 'https://test-services.resultsvault.com/RVVideoConnector_TEST_AUS',
        compConnectorOptions: {
          canExposeStreamGuid: 1,
          canExposeExtendedCompData: 0,
        },
      },
    },
    {
      customerGuid: '82b799c3-9e11-41b8-95ab-90c8162c3fd1',
      externalId: '101125',
      customerName: 'Usain Bolt',
      customerId: 1874,
      countrycode: 'UK',
      status: 1,
      hasUsers: false,
      customerTypeId: 0,
      customerTypeFlags: 0,
      timeZoneID: 26,
      realmID: 2,
      customerNameWithExtIDs: 'Usain Bolt',
      realm: {
        id: 1,
        name: 'ATHLETE',
        connectorUrl: 'https://test-services.resultsvault.com/RVVideoConnector_TEST_UK',
        compConnectorOptions: {
          canExposeStreamGuid: 1,
          canExposeExtendedCompData: 1,
        },
      },
    },
    {
      customerGuid: 'b5e2e85f-dc9e-4622-a0e4-dfc07ef47d52',
      externalId: '101126',
      customerName: 'Serena Williams',
      customerId: 1875,
      countrycode: 'AUS',
      status: 1,
      hasUsers: false,
      customerTypeId: 0,
      customerTypeFlags: 0,
      timeZoneID: 26,
      realmID: 2,
      customerNameWithExtIDs: 'Serena Williams',
      realm: {
        id: 1,
        name: 'ATHLETE',
        connectorUrl: 'https://test-services.resultsvault.com/RVVideoConnector_TEST_AUS',
        compConnectorOptions: {
          canExposeStreamGuid: 0,
          canExposeExtendedCompData: 1,
        },
      },
    },
    {
      customerGuid: '9d1bfa8a-4a2d-4cb6-9b0f-2b3f63a3456a',
      externalId: '101127',
      customerName: 'Lionel Messi',
      customerId: 1876,
      countrycode: 'UK',
      status: 1,
      hasUsers: false,
      customerTypeId: 0,
      customerTypeFlags: 0,
      timeZoneID: 26,
      realmID: 2,
      customerNameWithExtIDs: 'Lionel Messi',
      realm: {
        id: 1,
        name: 'ATHLETE',
        connectorUrl: 'https://test-services.resultsvault.com/RVVideoConnector_TEST_UK',
        compConnectorOptions: {
          canExposeStreamGuid: 1,
          canExposeExtendedCompData: 1,
        },
      },
    },
    {
      customerGuid: '3f75ed3b-cd26-4a3f-9b95-75d12f616f30',
      externalId: '101128',
      customerName: 'Simone Biles',
      customerId: 1877,
      countrycode: 'AUS',
      status: 1,
      hasUsers: false,
      customerTypeId: 0,
      customerTypeFlags: 0,
      timeZoneID: 26,
      realmID: 2,
      customerNameWithExtIDs: 'Simone Biles',
      realm: {
        id: 1,
        name: 'ATHLETE',
        connectorUrl: 'https://test-services.resultsvault.com/RVVideoConnector_TEST_AUS',
        compConnectorOptions: {
          canExposeStreamGuid: 0,
          canExposeExtendedCompData: 1,
        },
      },
    },
  ];

  const session = await getCurrentSession()
  if (!session.session) return []
  return mockCustomers

  // const url = `${import.meta.env.VITE_API_URL}/customers`

  // const response = await fetch(url, {
  //   headers: {
  //     Authorization: `Bearer ${session.session.getAccessToken().getJwtToken()}`,
  //   },
  // })
  // if (!response.ok) {
  //   // eslint-disable-next-line no-console
  //   console.error(response.status)
  //   return []
  // }

  // const data = await response.json()
  // if (data.error) {
  //   // eslint-disable-next-line no-console
  //   console.error(data.error)
  //   return []
  // }

  // return data
}

export const UserContext = createContext<ContextType>({} as ContextType)

export const UserContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isSignedIn, setIsSignedIn] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer>()
  const [prevVersionUsed, setPrevVersionUsed] = useState(window.localStorage.getItem('fbx-prev-version') ?? '')

  // Get the current user session every 12 hours
  const {
    data: userSession,
    fetchStatus: sessionFetchStatus,
    refetch: fetchSession,
  } = useQuery<UserSession, Error>({
    queryKey: ['currentSession'],
    queryFn: () => getCurrentSession(true),
    staleTime: 1000 * 60 * 60 * 12, // 12 hours - cognito JWT expiry length
    refetchOnWindowFocus: false,
    suspense: true,
  })
  const isFetched = sessionFetchStatus === 'idle'

  const signIn = useCallback(
    async ({ username, password }: { username: string; password: string }) => {
      const result = await Auth.signIn(username, password)
      fetchSession()
      return result
    },
    [fetchSession]
  )

  const signOut = async () => {
    return await Auth.signOut()
  }

  // Fetch the current user's customers
  const { data: customersData = [], refetch: fetchCustomers } = useQuery<Customer[], Error>({
    staleTime: Infinity,
    queryKey: ['customers'],
    queryFn: () => getCustomers(),
  })

  useEffect(() => {
    setIsSignedIn(isFetched && !!userSession)
  }, [isFetched, userSession])

  // Initialise Datadog and Freshchat with user data
  useEffect(() => {
    if (isFetched) {
      if (userSession?.session) fetchCustomers()
      if (userSession?.user) {
        datadogRum.setUser({
          id: userSession.user.sub,
          name: `${userSession.user.given_name} ${userSession.user.family_name}`,
          email: userSession.user.email,
        })
      }
    }
  }, [userSession, fetchCustomers, isFetched])

  // Ensure the selected customer is persisted in local storage
  useEffect(() => {
    if (customersData?.length) {
      const persistedCustomerGuid = window.localStorage.getItem('fbx-customer')
      const persistedCustomer =
        persistedCustomerGuid && customersData.find(customer => customer.customerGuid === persistedCustomerGuid)
      setSelectedCustomer(persistedCustomer || customersData[0])
      if (!persistedCustomerGuid && customersData[0].customerGuid) {
        window.localStorage.setItem('fbx-customer', customersData[0].customerGuid)
      }
    }
  }, [customersData])

  const setSelectedCustomerInner = (customer: Customer) => {
    if (!customer.customerGuid) {
      throw new Error(`Customer ${customer.customerName} has no GUID`)
    }
    setSelectedCustomer(customer)
    window.localStorage.setItem('fbx-customer', customer.customerGuid)
  }

  const contextValue: ContextType = useMemo(
    () => ({
      isSignedIn,
      signIn,
      signOut,
      session: userSession?.session,
      user: userSession?.user,
      reloadUser: fetchSession,
      shouldNotifyNewVersion: false,
      setPrevVersionUsed,
      customers: customersData,
      selectedCustomer,
      setSelectedCustomer: setSelectedCustomerInner,
    }),
    [
      customersData,
      fetchSession,
      isSignedIn,
      prevVersionUsed,
      selectedCustomer,
      signIn,
      userSession?.session,
      userSession?.user,
    ]
  )
  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
}

export const useUserContext = (): ContextType => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserSession must be used within an AuthContextProvider')
  }
  return context
}
